import { ButtonType, ButtonWidget, DialogType, TextLinkKind, TextLinkWidget } from '@tableau/widgets';
import * as React from 'react';
import ReactMarkdown = require('react-markdown');
import _ = require('underscore');

import { Formatters, Messages } from '~src/codegen/Localize';
import { CommonStyles } from '~src/common/react/CommonStyles';
import { imgPath } from '~src/common/utilities/ImgUrl';
import { openReactConfirmationDialog } from '~src/commonInfra/dialogs/ReactConfirmationDialog';
import { ModalInstance, ReactModalService } from '~src/commonInfra/dialogs/ReactModalService';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { AddOAuthCredentialAction } from '~src/connectivity/services/AddOAuthCredentialAction';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { AddOAuthClientDialog, AddOAuthClientDialogProps, OAuthClientSetting } from '~src/settings/general-settings/components/sections/oauth-clients-registry/AddOAuthClientDialog';
import { addOAuthClients, deleteOAuthClients, fetchOAuthClients, renderOAuthClientListTableHeader, renderOAuthClientTableRow } from '~src/settings/general-settings/components/sections/oauth-clients-registry/OAuthClientsRegistry';
import { addOAuthClientDialogStyle } from '~src/settings/general-settings/styles/oauth-client/OAuthClientsRegistryStyles';
import { learnMoreStyle, SettingsStyles } from '~src/settings/styles/CommonStyles';
import { AddOAuthClientParams } from '~src/ts/ServerApi/requests/AddOAuthClientsRequest';
import { DeleteCredentialRequest } from '~src/ts/ServerApi/requests/DeleteCredentialRequest';
import { IGetOAuthClientsResult } from '~src/ts/ServerApi/requests/GetOAuthClientsRequest';
import { IListCredentialSummariesResult, ListCredentialSummariesRequest } from '~src/ts/ServerApi/requests/ListCredentialSummariesRequest';
import { ICredentialSummary } from '~src/ts/ServerApi/types/ICredentialSummary';
import { IOAuthClient } from '~src/ts/ServerApi/types/IOAuthClient';
import { OAuthCredentialTypes } from '~src/ts/ServerApi/types/IOAuthCredential';
import { ToasterService, ToastType } from '~src/ts/services/ToasterService';

import { integrationSettingConnectButton, integrationsSettingsPageHeader, slackAddOauthClientButton, slackConnectivityMainHeader, slackInfoText, slackLogo, slackSettingsHeaderContainer } from '../styles/IntegrationsSettingsStyles';
import { sendConnectToSlackTelemetry } from './SlackIntegrationSettingsTelemetry';

export const ConnectToSlack = 'connect-to-slack';

export interface SlackIntegrationSettingsState {
  connected: boolean;
  oAuthClients: OAuthClientSetting[];
  credentialSummary: ICredentialSummary;
  getSlackCredentialError: boolean;
  getOauthClientError: boolean;
}

export interface SlackIntegrationSettingsProps {
}

export class SlackIntegrationSettings extends React.Component<SlackIntegrationSettingsProps, SlackIntegrationSettingsState> {
  constructor(props: SlackIntegrationSettingsProps) {
    super(props);
    this.state = { connected: false, credentialSummary: undefined, oAuthClients: [], getSlackCredentialError: false, getOauthClientError: false };
  }

  componentDidMount() {
    this.updateOAuthClients();
    this.getSlackCredentials();
  }

  /**
   * Retrieve slack credential summary (if exists) for the given site,
   * and set the 'connected' state field accordingly
   */
  getSlackCredentials() {
    let getCredentialsResult = getSlackCredentialsSummary();
    getCredentialsResult.then((result: IListCredentialSummariesResult) => {
      if (result.errors?.length) {
        this.setState({ connected: false, getSlackCredentialError: true });
      }
      else if (result.credentialSummaries && result.credentialSummaries.length > 0) {
        this.setState({ connected: true, credentialSummary: result.credentialSummaries[0] });
      } else {
        this.setState({ connected: false });
      }
    });
  }

  /**
   * Retrieve the Slack OAuth client (if exists) which has been added to this site
   */
  private updateOAuthClients() {
    let oAuthClients: IOAuthClient[];
    let clientSettings: OAuthClientSetting[];
    let errorFetchingClients: boolean;
    fetchOAuthClients().then((result: IGetOAuthClientsResult) => {
      if (result.errors) {
        errorFetchingClients = true;
      } else {
        errorFetchingClients = false;
        oAuthClients = result.oauthClients.filter(client => client.type === OAuthCredentialTypes.Slack);
        clientSettings = oAuthClients.map((v: IOAuthClient) => {
          const clientSetting: OAuthClientSetting = {
            dbClass: v.type,
            instanceUrl: '',
            oauthClientId: v.clientId,
            oauthClientSecret: '',
            oauthClientRedirectUri: '',
            id: v.id
          };
          return clientSetting;
        });
      }
    }).then(() => {
      this.setState({ oAuthClients: clientSettings, getOauthClientError: errorFetchingClients });
    });
  }

  render() {
    return (<div>
      <h2 className={integrationsSettingsPageHeader}>{Messages.connectToSlack_settingTitle()}</h2>
      {this.renderSlackAppInfoSection()}
      {this.isTableauOnPrem() && this.renderOAuthSection()}
      {this.renderConnectToSlackSection()}
    </div>
    );
  }

  /**
   * General Tableau on Slack info section and learn more link 
   */
  renderSlackAppInfoSection() {
    const imageUrl = imgPath('Slack_Integration_Logo.svg');
    return (
      <div data-tb-test-id={`${ConnectToSlack}-info-section`}>
        <div className={slackSettingsHeaderContainer}>
          <img
            data-tb-test-id={`${ConnectToSlack}-logo`}
            className={slackLogo}
            src={imageUrl}
            alt={Messages.connectToSlack_slackLogo()}
          />
          <h3 data-tb-test-id={`${ConnectToSlack}-description-header`} className={slackConnectivityMainHeader}>{Messages.connectToSlack_descriptionHeader()}</h3>
        </div>
        <div className={SettingsStyles.indent}>
          <div data-tb-test-id={`${ConnectToSlack}-setting-description`} className={slackInfoText}>
            {Messages.connectToSlack_settingDescription()}
          </div>
          <ReactMarkdown
            data-tb-test-id={`${ConnectToSlack}-slack-privacy-info`}
            source={Messages.connectToSlack_privacyPolicy()}
          />
          <div className={slackInfoText} data-tb-test-id={`${ConnectToSlack}-required-section`}>
            <span><strong>{Messages.connectToSlack_required()}</strong></span>
            <span data-tb-test-id={`${ConnectToSlack}-required-section-description`}>
              {this.isTableauOnPrem() ? Messages.connectToSlack_requirementDescription_onprem() : Messages.connectToSlack_requirementDescription_online()}
            </span>
            <TextLinkWidget
              testId={`${ConnectToSlack}-info-section-learn-more-link`}
              kind={TextLinkKind.Standard}
              className={learnMoreStyle}
              href={HelpService.getHelpUrlByCategory('server-slack-config')}
              target={'_blank'}
              rel={'noreferrer noopener'}>
              {Messages.connectToSlack_LearnMore()}
            </TextLinkWidget>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Includes "Add OAuth Client" logic, user instructions, and table
   */
  renderOAuthSection() {
    return (
      <div data-tb-test-id={`${ConnectToSlack}-oauth-section`} className={SettingsStyles.indent}>
        <h4 data-tb-test-id={`${ConnectToSlack}-oauth-header`}>{Messages.connectToSlack_slackOAuthClientRegistry()}</h4>
        <span data-tb-test-id={`${ConnectToSlack}-oauth-description`} className={slackInfoText}>{Messages.connectToSlack_registerSlackOAuthClient()}</span>
        <TextLinkWidget
          testId={`${ConnectToSlack}-learn-more-link`}
          className={learnMoreStyle}
          kind={TextLinkKind.Standard}
          href={HelpService.getHelpUrlByCategory('server-oauth-registry')}
          target={'_blank'}
          rel={'noreferrer noopener'}>
          {Messages.connectToSlack_LearnMore()}
        </TextLinkWidget>
        {this.getAddOAuthClientButton()}
        {this.renderOAuthClientTable()}
      </div>
    );
  }

  getAddOAuthClientButton() {
    return <ButtonWidget
      buttonType={ButtonType.Outline}
      testId='add-oauth-client-on-site-button'
      className={slackAddOauthClientButton}
      onClick={() => this.openOAuthModal()}
      disabled={this.state && this.state.oAuthClients && this.state.oAuthClients.length > 0}
      type='button'>{Messages.addOAuthClient_action()}
    </ButtonWidget>;
  }

  handleAddOAuthClient(oauthClientSetting: OAuthClientSetting) {
    const oauthParams: AddOAuthClientParams = {
      dbClass: oauthClientSetting.dbClass,
      instanceUrl: oauthClientSetting.instanceUrl,
      clientId: oauthClientSetting.oauthClientId,
      clientSecret: oauthClientSetting.oauthClientSecret,
      redirectUri: oauthClientSetting.oauthClientRedirectUri
    };
    addOAuthClients([oauthParams]).then((result: IResult) => {
      if (result.errors) {
        ToasterService.instance.pop({ type: ToastType.Error, text: Messages.connectToSlack_addOauthClient_error() });
      } else {
        this.updateOAuthClients();
        ToasterService.instance.pop({ type: ToastType.Success, text: Messages.connectToSlack_addOauthClient_succes() });
      }
    });
  }

  openOAuthModal() {
    let modalInstance: ModalInstance;
    const dialogProps: AddOAuthClientDialogProps = {
      dismiss: () => modalInstance && modalInstance.dismiss(),
      close: (result: any) => modalInstance && modalInstance.close(result),
      addOAuthClientSubmit: (formikValues: OAuthClientSetting) => this.handleAddOAuthClient(formikValues),
      hardcodeConnectionType: OAuthCredentialTypes.Slack,
      showInstanceUrlSection: false
    };

    modalInstance = ReactModalService.open({
      type: DialogType.Uber,
      dialogContent: React.createElement(AddOAuthClientDialog, dialogProps),
      testId: 'addOAuthClientDialog',
      showCloseButton: true,
      dialogStyle: addOAuthClientDialogStyle,
      shouldNotSetDefaultWidth: true
    });

    modalInstance.result.catch((reason) => {
      if (reason === 'dismissDialog' || reason === 'cancel') {
        Promise.resolve();
      } else Promise.reject();
    });
  }

  renderOAuthClientTable() {
    const deleteAction = (id: string) => deleteOAuthClients([id]);
    const deleteClient = (client: OAuthClientSetting) => {
      let description = Messages.connectToSlack_deleteOauthClient_description();
      let actionName = Messages.connectToSlack_deleteOauthClient_actionName();
      let header = Messages.connectToSlack_deleteOauthClient_header();
      this.openDeleteConfirmationDialog(description, header, actionName, deleteAction, client.id).then((result) => {
        if (result.errors) {
          ToasterService.instance.pop({ type: ToastType.Error, text: Messages.connectToSlack_deleteOauthClient_error() });
        } else {
          this.updateOAuthClients();
          ToasterService.instance.pop({ type: ToastType.Success, text: Messages.connectToSlack_deleteOauthClient_success() });
        }
      });
    };
    let stateLoaded = this.state && this.state.oAuthClients;
    if (!stateLoaded) return;
    let displayMessage = this.state.getOauthClientError ? Messages.connectToSlack_oAuthClients_errorState() : Messages.connectToSlack_oauthTable_emptyState();
    let client = this.state.oAuthClients[0];
    let emptyState = _.isUndefined(client) || _.isNull(client);
    return (
      <table id='oauth-clients-list-table-id' className='tb-static-grid-table tb-static-grid-table-settings-width'>
        <thead>
          <tr>
            {renderOAuthClientListTableHeader(false)}
          </tr>
        </thead>
        <tbody id='oauth-clients-list-body-id'>
          {renderOAuthClientTableRow({ client, deleteClient, showInstanceUrlColumn: false }, emptyState, this.state.connected, displayMessage)}
        </tbody>
      </table>
    );
  }

  /**
   * Includes the button to connect to slack, instructions, and slack credentials table
   */
  renderConnectToSlackSection() {
    let isConnectToSlackButtonDisabled = false;
    if (this.isTableauOnPrem()) {
      isConnectToSlackButtonDisabled = (this.state.oAuthClients.length === 0 && !this.state.connected);
    }

    return (
      <div data-tb-test-id={`${ConnectToSlack}-section`} className={SettingsStyles.indent}>
        {this.renderConnectToSlackHeaderAndDescription()}
        <div>
          <ButtonWidget
            testId={`${ConnectToSlack}-button`}
            className={integrationSettingConnectButton}
            handleClick={() => { this.handleConnectToSlack(this.state.connected, this.state.credentialSummary && this.state.credentialSummary.luid); }}
            buttonType={ButtonType.Outline}
            disabled={isConnectToSlackButtonDisabled}
          >
            {(this.state.connected) ? Messages.connectToSlack_disconnect() : Messages.connectToSlack()}
          </ButtonWidget>
        </div>
        {this.renderSlackConnectionTable()}
      </div>
    );
  }

  renderConnectToSlackHeaderAndDescription() {
    if (!this.isTableauOnPrem()) {
      // In TOL we do not display a title + description above the Slack button.
      // Add a space above the "Connect to Slack" button
      return <div data-tb-test-id={`${ConnectToSlack}-line-height-space`} style={{ height: CommonStyles.Sizes.LineHeight }}></div>;
    }

    return (
      <div>
        <h4 data-tb-test-id={`${ConnectToSlack}-header`}>{Messages.connectToSlack()}</h4>
        <p data-tb-test-id={`${ConnectToSlack}-connect-instructions`}>{Messages.connectToSlack_connectToSlackWorkspaceDescription()}</p>
      </div>
    );
  }

  renderSlackConnectionTable() {
    let workspaceName = this.state.connected ? this.state.credentialSummary.target.targetEndpoint :
      this.state.getSlackCredentialError ? Messages.connectToSlack_slackCredentials_errorState() : Messages.connectToSlack_notConnectedToWorkspace();
    let connectedOn = '';
    if (this.state.connected) {
      let date: Date = new Date(this.state.credentialSummary.createdAt);
      let validDate = date && !isNaN(date.getTime());
      connectedOn = validDate ? Formatters.formatMediumDateTimeWithoutSeconds(date) : Messages.connectToSlack_connectedOn_errorState();
    }

    return (
      <table id='slack-connection-table-id' className='tb-static-grid-table tb-static-grid-table-settings-width'>
        <thead>
          <tr>
            {this.renderSlackConnectionTableHeader()}
          </tr>
        </thead>
        <tbody id='slack-connection-list-body-id'>
          <tr>
            <td><span id='slack-connection-workspace-name' className='tb-data-grid-cell-text tb-lr-padded-wide'>{workspaceName}</span></td>
            <td><span id='slack-connection-connected-on' className='tb-data-grid-cell-text tb-lr-padded-wide'>{connectedOn}</span></td>
          </tr>
        </tbody>
      </table>
    );
  }

  renderSlackConnectionTableHeader() {
    const tableHeaders = { [Messages.connectToSlack_slackWorkspace()]: '', [Messages.connectToSlack_connectedOn()]: '' };
    let header = Object.keys(tableHeaders);
    return header.map((key, index) => {
      return (<th key={index} className='tb-data-grid-headers-line'>
        <span className='tb-data-grid-header-text'>{key}</span>
      </th>);
    });
  }

  /**
   * When the user presses "Disconnect from Slack", the corresponding credential should be deleted
   * @param luid string that corresponds to the slack credential to be deleted
   * @returns Result of the delete call
   */
  disconnectFromSlack(luid: string): Promise<IResult> {
    const slackCredentialsRequest = new DeleteCredentialRequest({ luid });
    return WebHttpClient.requestPost(slackCredentialsRequest);
  }

  handleConnectToSlack(alreadyConnected: boolean, id?: string) {
    const sessionManagementService = getSessionManagementServiceInstance();
    if (alreadyConnected) {
      let description = Messages.connectToSlack_disconnectFromSlack_description();
      let actionName = Messages.connectToSlack_disconnectFromSlack_actionName();
      let header = Messages.connectToSlack_disconnectFromSlack_header();
      this.openDeleteConfirmationDialog(description, header, actionName, this.disconnectFromSlack, id).then((result) => {
        if (result.errors) {
          ToasterService.instance.pop({ type: ToastType.Error, text: Messages.connectToSlack_disconnectFromSlack_error() });
        } else {
          this.setState({ connected: false });
          ToasterService.instance.pop({ type: ToastType.Success, text: Messages.connectToSlack_disconnectFromSlack_success() });
          sendConnectToSlackTelemetry({
            userId: sessionManagementService.sessionUserId(),
            siteLuid: sessionManagementService.sessionSiteLuid(),
            connectToSlack: false
          });
        }
      });
    } else {
      AddOAuthCredentialAction.attemptAuth(OAuthCredentialTypes.Slack).then((result) => {
        if (result) {
          ToasterService.instance.pop({ type: ToastType.Success, text: Messages.connectToSlack_toastSuccessMsg() });
          sendConnectToSlackTelemetry({
            userId: sessionManagementService.sessionUserId(),
            siteLuid: sessionManagementService.sessionSiteLuid(),
            connectToSlack: true
          });
        }
        this.getSlackCredentials();
      });
    }
  }

  /**
   * A confirmation dialog used for destructive actions on this page:
   * 1. Disconnecting from slack (deleting slack credential)
   * 2. Deleting Slack OAuth client
   * @param description The body test for the dialog
   * @param header The dialog title
   * @param actionName Wording for the destructive action (e.g. "Delete")
   * @param deleteAction The handler function
   * @param id Parameter used for the destructive action (ID associated with deleted credential/OAuth client)
   * @returns Dialog result
   */
  openDeleteConfirmationDialog(description: string, header: string, actionName: string, deleteAction: (id: string) => PromiseLike<IResult>, id: string): Promise<IResult> {
    const confirmationDialog = openReactConfirmationDialog<string, IResult>({
      title: header,
      message: description,
      paramsForAction: id,
      actionName,
      isActionDelete: true,
      dialogWidth: TabDialogWidth.width375px,
      action: () => deleteAction(id)
    });
    return confirmationDialog.result;
  }

  private isTableauOnPrem() {
    const sessionManagementService = getSessionManagementServiceInstance();
    return !sessionManagementService.isOnlineEnvironment();
  }
}

function getSlackCredentialsSummary() {
  const slackCredentialsRequest = new ListCredentialSummariesRequest({ targetClass: OAuthCredentialTypes.Slack });
  return WebHttpClient.requestPost(slackCredentialsRequest);
}